import { useState, useEffect, useCallback } from 'react'
import { useDashboardStats } from '../../../hooks/useDashboardStats'
import { apiService } from '../../../services/api'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import QuickActions from '../shared/QuickActions'
import RealTimeResults from '../shared/RealTimeResults'
import IncompleteTotalsBanner from '../shared/IncompleteTotalsBanner'
import { UserGroupIcon, TrophyIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

/*
 * Mirrors `GET /api/analytics/coach/overview` (backend/src/routes/analytics.ts),
 * which returns `{ schoolRanking, positionGroups, athletes }` and nothing else
 * (#160). This used to read `topPerformers`, `trainingGoals`, `upcomingEvents`,
 * `stats`, and per-athlete `grade`, `recentImprovement` and `percentile` — none
 * of which the API returns — so the list showed "undefinedth", the Training
 * Goals and Upcoming Events panels were empty forever, and "Training Plans"
 * always counted 0. Panels with no source are gone rather than invented.
 */
interface RosterAthlete {
  id: string
  name: string
  position: string
  // Null when none of the athlete's results has been ranked yet.
  overallPercentile: number | null
  drills: Array<{ drillName: string; bestValue: number; percentile: number | null }>
}

interface CoachOverviewData {
  schoolRanking: { schoolName: string; compositeScore: number | null }
  positionGroups: Array<{ position: string; athleteCount: number; avgPercentile: number | null }>
  athletes: RosterAthlete[]
}

interface CoachDashboardProps {
  elevated?: boolean
}

export default function CoachDashboard({ elevated = false }: CoachDashboardProps) {
  const { stats, loading: statsLoading, error: statsError } = useDashboardStats()
  const [coachData, setCoachData] = useState<CoachOverviewData | null>(null)
  const [coachLoading, setCoachLoading] = useState(true)
  const [coachError, setCoachError] = useState<string | null>(null)
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null)
  const [selectedAthlete, setSelectedAthlete] = useState<RosterAthlete | null>(null)

  const fetchCoachData = useCallback(async () => {
    try {
      setCoachLoading(true)
      setCoachError(null)
      const response = await apiService.getCoachOverview()
      if (response.error) {
        setCoachError(response.message || 'Failed to load coach data')
        setCoachData(null)
      } else {
        setCoachData(response.data)
      }
    } catch {
      setCoachError('Failed to load coach data')
      setCoachData(null)
    } finally {
      setCoachLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCoachData()
  }, [fetchCoachData])

  const loading = statsLoading || coachLoading

  // Ranked athletes first, highest percentile first; unranked at the end.
  const topAthletes: RosterAthlete[] = [...(coachData?.athletes ?? [])].sort(
    (a, b) => (b.overallPercentile ?? -1) - (a.overallPercentile ?? -1)
  )
  const rankedCount = topAthletes.filter(a => a.overallPercentile !== null).length

  // Get unique position groups for drill-down
  const positionGroups = [...new Set(topAthletes.map(a => a.position))].sort()

  // Filter athletes by selected position
  const filteredAthletes = selectedPosition
    ? topAthletes.filter(a => a.position === selectedPosition)
    : topAthletes

  const coachStats = [
    {
      name: 'My Athletes',
      value: stats?.totalAthletes || topAthletes.length || 0,
      change: stats?.changes.totalAthletes,
      icon: UserGroupIcon,
    },
    {
      name: 'Ranked Athletes',
      value: coachData ? rankedCount : '--',
      change: undefined,
      icon: TrophyIcon,
    },
    {
      name: 'Position Groups',
      value: coachData ? coachData.positionGroups.length : '--',
      change: undefined,
      icon: ClockIcon,
    },
  ]

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) {
      return 'text-green-600'
    }
    if (percentile >= 75) {
      return 'text-blue-600'
    }
    if (percentile >= 50) {
      return 'text-yellow-600'
    }
    return 'text-gray-600'
  }

  const hasNoData = !coachLoading && !coachError && topAthletes.length === 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          Coach Dashboard
          {elevated && (
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              School-Wide Access
            </span>
          )}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Track your athletes' progress and manage team development
        </p>
      </div>

      {/* Error with retry */}
      {coachError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{coachError}</p>
          <button
            onClick={fetchCoachData}
            className="flex items-center space-x-1 text-sm text-red-700 hover:text-red-900 font-medium"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      )}

      {/*
        The stats hook's own failure was previously assigned to an unused
        `_error` and never shown, so a failed `useDashboardStats` was invisible
        on this dashboard while its tiles fell back to coach-scoped values.
      */}
      {statsError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" role="alert">
          <p className="text-sm text-red-700">{statsError}</p>
        </div>
      )}

      {/*
        `IncompleteTotalsBanner` says every hook that populates `gaps` must
        render it. This is the third consumer of `useDashboardStats`, and it was
        the one that did not — a coach whose event list lost a page saw an
        undercount with nothing saying so.
      */}
      {stats && <IncompleteTotalsBanner gaps={stats.gaps} />}

      {/* Coach Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {coachStats.map((stat, index) => (
          <StatCard
            key={index}
            name={stat.name}
            value={stat.value}
            change={stat.change}
            icon={stat.icon}
            loading={loading}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Performers */}
        <div className="lg:col-span-2">
          <DashboardCard
            title="Your Athletes"
            loading={coachLoading}
            error={undefined}
            actions={
              <button
                className="btn-secondary text-sm"
                onClick={() => (window.location.href = '/athletes')}
              >
                View All
              </button>
            }
          >
            {hasNoData ? (
              <div className="text-center py-8">
                <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No combine data yet</p>
                <p className="text-gray-400 text-xs mt-1">
                  Athletes will appear here after their first combine event
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Position group filter */}
                {positionGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        !selectedPosition
                          ? 'bg-primary-100 text-primary-800'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setSelectedPosition(null)
                        setSelectedAthlete(null)
                      }}
                    >
                      All
                    </button>
                    {positionGroups.map(pos => (
                      <button
                        key={pos}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          selectedPosition === pos
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          setSelectedPosition(pos)
                          setSelectedAthlete(null)
                        }}
                      >
                        {pos}
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected athlete detail card */}
                {selectedAthlete && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {selectedAthlete.name}
                      </h4>
                      <button
                        onClick={() => setSelectedAthlete(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Close
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Position:</span>
                        <span className="ml-1 font-medium">{selectedAthlete.position}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Percentile:</span>
                        <span
                          className={`ml-1 font-semibold ${
                            selectedAthlete.overallPercentile !== null
                              ? getPercentileColor(selectedAthlete.overallPercentile)
                              : 'text-gray-500'
                          }`}
                        >
                          {selectedAthlete.overallPercentile !== null
                            ? `${Math.round(selectedAthlete.overallPercentile)}th`
                            : 'Unranked'}
                        </span>
                      </div>
                    </div>
                    {selectedAthlete.drills.length > 0 && (
                      <ul className="mt-3 space-y-1 text-sm">
                        {selectedAthlete.drills.map(drill => (
                          <li key={drill.drillName} className="flex justify-between">
                            <span className="text-gray-600">
                              {drill.drillName}: {drill.bestValue}
                            </span>
                            <span className="text-gray-500">
                              {drill.percentile !== null
                                ? `${Math.round(drill.percentile)}th`
                                : 'unranked'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {filteredAthletes.map(athlete => (
                  <div
                    key={athlete.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedAthlete(athlete)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-900">{athlete.name}</h4>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {athlete.position}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {athlete.drills.length === 1
                            ? '1 drill recorded'
                            : `${athlete.drills.length} drills recorded`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Percentile</p>
                          <p
                            className={`text-sm font-semibold ${
                              athlete.overallPercentile !== null
                                ? getPercentileColor(athlete.overallPercentile)
                                : 'text-gray-400'
                            }`}
                          >
                            {athlete.overallPercentile !== null
                              ? `${Math.round(athlete.overallPercentile)}th`
                              : 'Unranked'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DashboardCard>
        </div>

        {/* Quick Actions */}
        <DashboardCard title="Quick Actions">
          <QuickActions />
        </DashboardCard>
      </div>

      {/* Additional Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live Results — no event preselected at the dashboard level. The
            socket still connects, but without an eventId it joins no room, so
            the widget sits in its connected/idle "waiting for live results"
            state instead of erroring on a nonexistent 'sample-event-id' room. */}
        <RealTimeResults maxResults={6} showConnectionStatus={true} />
      </div>
    </div>
  )
}
